/**
 * Utility functions used by Spreadsheet.tsx and related modules.
 * Constants and types are in spreadsheetConstants.ts and spreadsheetTypes.ts.
 */

import {
  createColumn,
  deleteCells,
  deleteColumn,
  displayColumnName,
  DOMAIN_COLUMN_NAME,
  getCells,
  getColumns,
  isLockedColumn,
  isPrimaryColumnName,
  isPrioritizationColumnName,
  LEAD_COLUMN_NAME,
  LEAD_COMPANY_COLUMN_NAME,
  putCells,
  reorderColumn,
  PRIMARY_COLUMN_NAMES
} from '../../api/spreadsheet';
import type { Cell, ColumnDefinition, Column } from '../../api/spreadsheet';
import type { CampaignSpreadsheetViewType } from '../../context/CampaignContext';
import type { CellValidationError } from '../../utils/spreadsheetValidation';
import {
  formatListValueForDisplay,
  normalizeListValueForBackend,
  normalizePythonType,
  validateSheetCells
} from '../../utils/spreadsheetValidation';
import {
  AI_COLUMN_EXTRA_WIDTH,
  CELL_MEASURE_FONT,
  COLUMN_WIDTH_MAX,
  COLUMN_WIDTH_MIN,
  COLUMN_WIDTH_PADDING,
  FAVICON_MIN_SIZE,
  FAVICON_SIZE,
  FIRST_COL_FAVICON_PADDING,
  LIST_DISPLAY_SEPARATOR
} from './spreadsheetConstants';
import type {
  ColumnEditorInfo,
  ColumnSchemaWithData,
  HeaderStateLike,
  SheetLike,
  SpreadsheetColumnsChangedDetail,
  SyncSheetToBackendApiLike,
  UniverSheetAPILike,
  ColumnSchemaOverridesLike
} from './spreadsheetTypes';
import type { SyncBufferState } from './spreadsheetSyncBuffer';
import {
  takeSyncBufferSnapshot,
  SYNC_BUFFER_CELL_KEY
} from './spreadsheetSyncBuffer';
import { CellValueType } from '@univerjs/core';

export type {
  ColumnEditorInfo,
  ColumnSchemaWithData,
  HeaderStateLike,
  SheetLike,
  SpreadsheetColumnsChangedDetail,
  SyncSheetToBackendApiLike,
  UniverSheetAPILike,
  ColumnSchemaOverridesLike
};
export {
  AI_COLUMN_EXTRA_WIDTH,
  CELL_MEASURE_FONT,
  COLUMN_WIDTH_MAX,
  COLUMN_WIDTH_MIN,
  COLUMN_WIDTH_PADDING,
  FAVICON_MIN_SIZE,
  FAVICON_SIZE,
  FIRST_COL_FAVICON_PADDING,
  LIST_DISPLAY_SEPARATOR,
  MAX_SPREADSHEET_COLUMNS,
  MAX_SPREADSHEET_ROWS
} from './spreadsheetConstants';
export { LOCKED_COLUMN_ERROR_MSG } from './spreadsheetConstants';

export function getColTypeFromSchema(
  column: ColumnSchemaWithData | undefined
): string | undefined {
  return column?.col_type ?? column?.column_data?.col_type;
}

/** Columns array for validation (backend returns all columns including $Domain Name). */
export function getColumnsWithTypes(
  columns: ColumnSchemaWithData[]
): { col_name: string; col_type: string }[] {
  return columns.map((c) => ({
    col_name: c.col_name,
    col_type: getColTypeFromSchema(c) ?? 'str'
  }));
}

/** Get AI enrichment definition from column (top-level or column_data.definition). */
export function getColumnDefinition(
  c: (Column & { column_data?: { definition?: ColumnDefinition } }) | undefined
): ColumnDefinition | undefined {
  return c?.definition ?? c?.column_data?.definition;
}

/** True when the column has a non-empty enrichment prompt (preset label or custom text). */
export function columnDefinitionHasEnrichmentPrompt(
  def: ColumnDefinition | undefined
): boolean {
  return (
    def != null && typeof def.value === 'string' && def.value.trim().length > 0
  );
}

/**
 * Merge client header column with GET /columns row by name. Backend wins so schema matches API when
 * local state is stale (e.g. defined_signal presets saved but header state not refreshed).
 */
function mergeColumnWithBackend(
  colIndex: number,
  columnNamesByIndex: string[],
  clientColumns:
    | (Column & { column_data?: { definition?: ColumnDefinition } })[]
    | undefined,
  backendColumns: Column[] | undefined
): (Column & { column_data?: { definition?: ColumnDefinition } }) | undefined {
  const client = clientColumns?.[colIndex];
  const name = columnNamesByIndex[colIndex];
  if (name == null) return client;
  const backend = backendColumns?.find((b) => b.col_name === name);
  if (!backend) return client;
  return { ...(client ?? {}), ...backend } as Column & {
    column_data?: { definition?: ColumnDefinition };
  };
}

/** Message when user tries to delete the domain column. */
export function getDomainColumnDeleteMessage(): string {
  return `The ${displayColumnName(DOMAIN_COLUMN_NAME)} column cannot be deleted.`;
}

/**
 * Parse a string that looks like a list (JSON array or Python-style ['a','b']) into string items.
 * Returns null if the string does not look like a list or parsing fails.
 */
export function parseListLikeString(s: string): string[] | null {
  const trimmed = s.trim();
  if (
    trimmed.length < 2 ||
    trimmed[0] !== '[' ||
    trimmed[trimmed.length - 1] !== ']'
  )
    return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.map((item) => (item == null ? '' : String(item)));
  } catch {
    const items: string[] = [];
    const re = /['"]([^'"]*)['"]/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(trimmed)) !== null) items.push(m[1] ?? '');
    return items.length > 0 ? items : null;
  }
}

/**
 * Normalize any value to a string safe for cell display and backend.
 * Supports list format: arrays and Python-style list strings are shown as "a, b".
 */
export function normalizeCellValue(val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'string') {
    if (val === '[object Object]') return '';
    const listItems = parseListLikeString(val);
    if (listItems) return listItems.join(LIST_DISPLAY_SEPARATOR);
    return val;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    if (Array.isArray(obj)) {
      return obj
        .map((item) => normalizeCellValue(item))
        .join(LIST_DISPLAY_SEPARATOR);
    }
    if ('p' in obj && obj.p != null && typeof obj.p === 'object') {
      const body = (obj.p as Record<string, unknown>)?.body as
        | { dataStream?: string }
        | undefined;
      const stream = body?.dataStream;
      if (typeof stream === 'string') {
        return stream.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      }
    }
    if ('v' in obj || 's' in obj || 't' in obj) {
      const v = obj.v;
      return v == null || v === '' ? '' : normalizeCellValue(v);
    }
    return '';
  }
  return String(val);
}

let _measureCanvas: HTMLCanvasElement | null = null;

/** Measure text width in px using canvas. Caches canvas in module scope. */
export function measureTextWidth(
  text: string,
  font = CELL_MEASURE_FONT
): number {
  if (text == null || String(text).trim() === '') return 0;
  const str = String(text);
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas');
  const ctx = _measureCanvas.getContext('2d');
  if (!ctx) return str.length * 7;
  ctx.font = font;
  return ctx.measureText(str).width;
}

/** Compute column widths from max text content (headers + cell values). AI and favicon columns get extra width. */
export function computeColumnWidths(
  columnNamesByIndex: string[],
  cells: Cell[],
  columns?: (Column & { column_data?: { definition?: ColumnDefinition } })[]
): Record<number, { w: number }> {
  const result: Record<number, { w: number }> = {};
  for (let i = 0; i < columnNamesByIndex.length; i++) {
    const raw = columnNamesByIndex[i] ?? '';
    const headerText = displayColumnName(raw);
    let maxW = measureTextWidth(headerText);
    const colName = columnNamesByIndex[i];
    const isDomainCol = colName === DOMAIN_COLUMN_NAME;
    const isFaviconCol =
      colName === DOMAIN_COLUMN_NAME || colName === LEAD_COMPANY_COLUMN_NAME;
    const isListCol =
      columns?.[i] &&
      normalizePythonType(
        getColTypeFromSchema(columns[i] as ColumnSchemaWithData)
      ) === 'list';
    for (const cell of cells) {
      const matches =
        cell.column_name === colName ||
        (isDomainCol && cell.column_name === DOMAIN_COLUMN_NAME);
      if (matches && cell.cell_value != null && cell.cell_value !== '') {
        const displayStr = isListCol
          ? formatListValueForDisplay(String(cell.cell_value))
          : normalizeCellValue(cell.cell_value);
        if (displayStr) {
          const w = measureTextWidth(displayStr);
          if (w > maxW) maxW = w;
        }
      }
    }
    let width = Math.ceil(maxW) + COLUMN_WIDTH_PADDING;
    if (isFaviconCol) width += FAVICON_SIZE + FIRST_COL_FAVICON_PADDING;
    const isAiCol =
      columns?.[i] &&
      (columns[i].definition ?? columns[i].column_data?.definition);
    if (isAiCol) width += AI_COLUMN_EXTRA_WIDTH;
    width = Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, width));
    result[i] = { w: width };
  }
  return result;
}

/**
 * Re-apply auto column widths from current sheet cell values for the given indices.
 * Matches computeColumnWidths rules (header, list display, favicon, AI extras, min/max).
 * Used after backend-driven set-range-values (e.g. enrichment) so columns grow without a full refresh.
 */
export function applyColumnWidthsForIndicesFromSheet(
  api: SyncSheetToBackendApiLike,
  columnNamesByIndex: string[],
  columns:
    | (Column & { column_data?: { definition?: ColumnDefinition } })[]
    | undefined,
  colIndices: readonly number[],
  backendColumns?: Column[]
): void {
  const unique = new Set<number>();
  for (const c of colIndices) {
    if (typeof c === 'number' && c >= 0 && c < columnNamesByIndex.length)
      unique.add(c);
  }
  if (unique.size === 0) return;
  const fWorkbook = api.getActiveWorkbook?.();
  const fWorksheet = fWorkbook?.getActiveSheet?.();
  if (!fWorksheet || typeof fWorksheet.setColumnWidth !== 'function') return;
  const sheet = fWorksheet.getSheet?.();
  if (!sheet || typeof sheet.getCellValueOnly !== 'function') return;
  const rowCount = sheet.getConfig()?.rowCount ?? 0;
  if (rowCount <= 0) return;
  const uniqueCols = Array.from(unique);
  for (let i = 0; i < uniqueCols.length; i++) {
    const c = uniqueCols[i]!;
    const colMeta = mergeColumnWithBackend(
      c,
      columnNamesByIndex,
      columns,
      backendColumns
    );
    const raw = columnNamesByIndex[c] ?? '';
    const headerText = displayColumnName(raw);
    let maxW = measureTextWidth(headerText);
    const colName = columnNamesByIndex[c];
    const isFaviconCol =
      colName === DOMAIN_COLUMN_NAME || colName === LEAD_COMPANY_COLUMN_NAME;
    for (let row = 0; row < rowCount; row++) {
      const rawVal = sheet.getCellValueOnly(row, c);
      let displayVal = normalizeCellValue(rawVal ?? '');
      const colType = colMeta
        ? getColTypeFromSchema(colMeta as ColumnSchemaWithData)
        : undefined;
      if (normalizePythonType(colType) === 'list') {
        displayVal = formatListValueForDisplay(displayVal);
      }
      if (!displayVal || String(displayVal).trim() === '') continue;
      const w = measureTextWidth(String(displayVal));
      if (w > maxW) maxW = w;
    }
    let width = Math.ceil(maxW) + COLUMN_WIDTH_PADDING;
    if (isFaviconCol) width += FAVICON_SIZE + FIRST_COL_FAVICON_PADDING;
    const isAiCol = colMeta && getColumnDefinition(colMeta);
    if (isAiCol) width += AI_COLUMN_EXTRA_WIDTH;
    width = Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, width));
    fWorksheet.setColumnWidth(c, width);
  }
}

/** Convert 0-based column index to A1-style column letter(s) (0=A, 26=AA, etc.). */
export function colToA1Letter(col: number): string {
  let n = col + 1;
  let s = '';
  while (n > 0) {
    n--;
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26);
  }
  return s || 'A';
}

/** Extract hostname from a URL or domain string for favicon and display. */
export function getDomainHost(value: string | null | undefined): string | null {
  if (value == null || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const host = new URL(url).hostname;
    return host || null;
  } catch {
    return trimmed || null;
  }
}

/** DuckDuckGo favicon URL for a domain. */
export function getFaviconUrl(domain: string): string {
  return `https://icons.duckduckgo.com/ip3/${encodeURIComponent(domain)}.ico`;
}

const faviconImageCache = new Map<string, HTMLImageElement | null>();
let onFaviconLoaded: (() => void) | null = null;

/** Register callback to run when a favicon finishes loading (so the sheet can repaint). */
export function setFaviconLoadedCallback(cb: (() => void) | null): void {
  onFaviconLoaded = cb;
}

/** Load favicon for domain; returns cached image or null while loading. Calls onFaviconLoaded when done. */
export function loadFavicon(domain: string): HTMLImageElement | null {
  const cached = faviconImageCache.get(domain);
  if (cached !== undefined) return cached;
  const img = new Image();
  faviconImageCache.set(domain, img);
  img.onload = () => {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (w >= FAVICON_MIN_SIZE && h >= FAVICON_MIN_SIZE) {
      faviconImageCache.set(domain, img);
      onFaviconLoaded?.();
    } else {
      faviconImageCache.set(domain, null);
    }
  };
  img.onerror = () => {
    faviconImageCache.set(domain, null);
  };
  img.src = getFaviconUrl(domain);
  return null;
}

/** Build column name -> column index map from columns array. */
export function getColumnIndexMapping(columns: Column[]): Map<string, number> {
  const output = new Map<string, number>();
  columns.forEach((column, i) => {
    output.set(column.col_name, column.col_idx ?? i);
  });
  return output;
}

/** Returns a name not in existingNames; if baseName is taken, appends " {1}", " {2}", etc. */
export function getUniqueColumnName(
  baseName: string,
  existingNames: string[]
): string {
  const set = new Set(existingNames);
  if (!set.has(baseName)) return baseName;
  let i = 1;
  while (set.has(`${baseName} {${i}}`)) i++;
  return `${baseName} {${i}}`;
}

/**
 * Sort columns for display when loading from backend. Uses col_idx from backend (order is backend-owned).
 */
export function sortColumnsForLoad(columns: Column[]): Column[] {
  return [...columns].sort((a, b) => (a.col_idx ?? 0) - (b.col_idx ?? 0));
}

/**
 * Ensures each column's col_idx equals its array index. Mutates columns in place.
 * Returns which columns needed updating (for optional backend persist).
 */
export function normalizeColumnIndices(columns: Column[]): {
  changed: boolean;
  updates: { columnName: string; newIdx: number }[];
} {
  const updates: { columnName: string; newIdx: number }[] = [];
  columns.forEach((col, idx) => {
    const currentIdx = col.col_idx ?? idx;
    if (currentIdx !== idx) {
      updates.push({ columnName: col.col_name, newIdx: idx });
      col.col_idx = idx;
    }
  });
  return { changed: updates.length > 0, updates };
}

/**
 * Re-key index-based maps (columnNameOverrides, columnSchemaOverrides) when columns are inserted or removed.
 * Insert at startColumn: indices >= startColumn shift right by count.
 * Remove at startColumn for count columns: indices in [startColumn, startColumn+count) dropped; indices above shift left.
 */
export function shiftColumnIndexKeyedRecord<T>(
  map: Record<number, T> | undefined,
  event: SpreadsheetColumnsChangedDetail
): Record<number, T> {
  if (!map || Object.keys(map).length === 0) return {};
  const out: Record<number, T> = {};
  for (const [kStr, v] of Object.entries(map)) {
    const k = parseInt(kStr, 10);
    if (Number.isNaN(k)) continue;
    if (event.kind === 'reorder-cols') {
      const newIdx = event.oldToNew[k];
      if (typeof newIdx === 'number' && newIdx >= 0) {
        out[newIdx] = v;
      }
    } else if (event.kind === 'insert-col') {
      const { startColumn, count } = event;
      if (k >= startColumn) {
        out[k + count] = v;
      } else {
        out[k] = v;
      }
    } else {
      const { startColumn, count } = event;
      if (k < startColumn) {
        out[k] = v;
      } else if (k >= startColumn + count) {
        out[k - count] = v;
      }
    }
  }
  return out;
}

/** Move a contiguous block of columns to match Univer `move-cols`: `insertAt` is pre-move grid index
 * (toRange.startColumn). After `splice(fromS,n)`, indices to the right shift by n, so insertion when
 * moving right must be `insertAt - n`. Mutates `arr`.
 * */
export function applyColumnBlockMoveInPlace<T>(
  arr: T[],
  fromS: number,
  fromE: number,
  insertAt: number
): void {
  const n = fromE - fromS + 1;
  if (n <= 0 || fromS < 0 || insertAt < 0) return;
  const block = arr.splice(fromS, n);
  const dest = insertAt > fromS ? insertAt - n : insertAt;
  arr.splice(dest, 0, ...block);
}

/**
 * After a column block move, maps each old column index to its new index.
 * Length must match the column count before/after the move.
 */
export function buildOldToNewColumnIndexMap(
  columnCount: number,
  fromS: number,
  fromE: number,
  insertAt: number
): number[] {
  if (columnCount <= 0) return [];
  const identity = Array.from({ length: columnCount }, (_, i) => i);
  applyColumnBlockMoveInPlace(identity, fromS, fromE, insertAt);
  const oldToNew = new Array<number>(columnCount).fill(-1);
  for (let newIdx = 0; newIdx < columnCount; newIdx++) {
    oldToNew[identity[newIdx]] = newIdx;
  }
  return oldToNew;
}

/** Rightmost sheet index among primary ($) columns that exist, or -1 if none. */
export function getRightmostPrimaryColumnIndex(
  columnNamesByIndex: string[]
): number {
  let max = -1;
  for (const name of PRIMARY_COLUMN_NAMES) {
    const idx = columnNamesByIndex.indexOf(name);
    if (idx >= 0 && idx > max) max = idx;
  }
  return max;
}

// ---------------------------------------------------------------------------
// Ref-based helpers: pass ref.current (or current values) as arguments.
// This keeps utils pure and testable; the component reads refs at call time.
// ---------------------------------------------------------------------------

/**
 * Build column editor info for a given column index.
 * Pass headerStateRef.current (or null).
 */
export function getColumnEditorInfoForColumn(
  col: number,
  headerState: HeaderStateLike | null
): ColumnEditorInfo | null {
  if (!headerState || col < 0 || col >= headerState.columnNamesByIndex.length)
    return null;
  const { columnNamesByIndex, columns } = headerState;
  const columnName = columnNamesByIndex[col] ?? `Column ${col}`;
  const columnSchema = columns[col] as ColumnSchemaWithData | undefined;
  return {
    columnIndex: col,
    columnName,
    allColumnNames: [...columnNamesByIndex],
    columnType: getColTypeFromSchema(columnSchema),
    description:
      columnSchema?.description ?? columnSchema?.column_data?.description,
    definition: getColumnDefinition(columnSchema)
  };
}

/** Run validation on initial cell data and return errors. Caller sets validationErrorsMapRef, setValidationErrors, then applies styles from the map via applyValidationErrorStylesToUniverCells. */
export function applyInitialValidationToUniverCells(
  columnsWithTypes: { col_name: string; col_type: string }[],
  cellMatrix: Record<number, Record<number, unknown>>
): CellValidationError[] {
  return validateSheetCells(columnsWithTypes, cellMatrix);
}

/** Apply invalid-cell style to univerCells for each error (e.g. after setting validationErrorsMapRef from initial validation). */
export function applyValidationErrorStylesToUniverCells(
  univerCells: Record<number, Record<number, { s?: Record<string, unknown> }>>,
  errors: CellValidationError[],
  invalidCellStyle: Record<string, unknown>
): void {
  for (const e of errors) {
    if (!univerCells[e.row]) univerCells[e.row] = {};
    const existing = univerCells[e.row][e.col];
    univerCells[e.row][e.col] = {
      ...existing,
      s: { ...(existing?.s || {}), ...invalidCellStyle }
    };
  }
}

/** Per-column header config for Univer `customizeColumnHeader({ columnsCfg })`. */
export type ColumnHeaderColumnsCfg = Record<
  number,
  string | { text: string; backgroundColor?: string }
>;

/** Build column header labels (display names) from column names by index. Strips leading $ for display. */
export function buildColumnHeaderLabels(
  columnNamesByIndex: string[]
): Record<number, string> {
  const labels: Record<number, string> = {};
  columnNamesByIndex.forEach((name, i) => {
    const raw = name ?? `Column ${i}`;
    labels[i] = raw.startsWith('$')
      ? displayColumnName(raw.slice(1))
      : displayColumnName(raw);
  });
  return labels;
}

export type BuildColumnHeaderColumnsCfgOptions = {
  spreadsheetViewType?: CampaignSpreadsheetViewType;
  /**
   * On the **Leads** spreadsheet tab, `$Lead` and `$Company` use this fill so they match the
   * visible primary styling from the Accounts tab (`campaignChromeLeadBg` is too close to the
   * default header gray).
   */
  leadPrimaryMatchAccountsChromeBg?: string;
  /** Header fill for prioritization columns ($Qualified, $Reason, $Score, $Tier). */
  prioritizationColumnHeaderBg?: string;
};

/**
 * Like {@link buildColumnHeaderLabels}, but primary key columns get a full-cell header
 * `backgroundColor` (drawn by Univer under the label, not as a canvas overlay).
 */
export function buildColumnHeaderColumnsCfg(
  columnNamesByIndex: string[],
  primaryColumnHeaderBg: string,
  options?: BuildColumnHeaderColumnsCfgOptions
): ColumnHeaderColumnsCfg {
  const cfg: ColumnHeaderColumnsCfg = {};
  const view = options?.spreadsheetViewType;
  const leadMatchAccounts = options?.leadPrimaryMatchAccountsChromeBg;
  const prioritizationBg = options?.prioritizationColumnHeaderBg;

  columnNamesByIndex.forEach((name, i) => {
    const raw = name ?? `Column ${i}`;
    const text = raw.startsWith('$')
      ? displayColumnName(raw.slice(1))
      : displayColumnName(raw);

    let backgroundColor: string | undefined;

    if (
      view === 'lead' &&
      typeof leadMatchAccounts === 'string' &&
      leadMatchAccounts.length > 0 &&
      (raw === LEAD_COLUMN_NAME || raw === LEAD_COMPANY_COLUMN_NAME)
    ) {
      backgroundColor = leadMatchAccounts;
    } else if (isPrimaryColumnName(raw)) {
      backgroundColor = primaryColumnHeaderBg;
    } else if (
      typeof prioritizationBg === 'string' &&
      prioritizationBg.length > 0 &&
      isPrioritizationColumnName(raw)
    ) {
      backgroundColor = prioritizationBg;
    }

    if (typeof backgroundColor === 'string' && backgroundColor.length > 0) {
      cfg[i] = { text, backgroundColor };
    } else {
      cfg[i] = text;
    }
  });
  return cfg;
}

/** Apply column name overrides to header state (mutates state.columnNamesByIndex and matching columns[].col_name). */
export function applyColumnNameOverridesToHeaderState(
  state: {
    columnNamesByIndex: string[];
    columns?: Column[];
    refreshHeaderBar?: () => void;
  },
  overrides: Record<number, string> | null | undefined
): void {
  if (!overrides || Object.keys(overrides).length === 0) return;
  for (const [idxStr, name] of Object.entries(overrides)) {
    const idx = parseInt(idxStr, 10);
    if (
      !Number.isNaN(idx) &&
      idx >= 0 &&
      idx < state.columnNamesByIndex.length
    ) {
      state.columnNamesByIndex[idx] = name;
      if (state.columns && idx < state.columns.length) {
        const prev = state.columns[idx];
        state.columns[idx] = { ...prev, col_name: name, col_idx: idx };
      }
    }
  }
  state.refreshHeaderBar?.();
}

/** Apply column schema overrides to header state (mutates state.columns). Does not re-apply to Univer sheet. */
export function applyColumnSchemaOverridesToHeaderState(
  state: { columns: Column[] } | null,
  overrides:
    | Record<
        number,
        {
          col_type?: string;
          description?: string;
          definition?: ColumnDefinition;
        }
      >
    | null
    | undefined
): void {
  if (!overrides || !state?.columns || Object.keys(overrides).length === 0)
    return;
  for (const [idxStr, schema] of Object.entries(overrides)) {
    const idx = parseInt(idxStr, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= state.columns.length) continue;
    const prev = state.columns[idx];
    state.columns[idx] = {
      ...prev,
      ...(schema.col_type != null && { col_type: schema.col_type }),
      ...('description' in schema && { description: schema.description }),
      ...('definition' in schema && { definition: schema.definition })
    };
  }
}

/** Ensure every row in the payload has a domain cell; add from sheet when missing. getCellValue(row, col) returns raw cell value. */
export function ensureDomainForRowsInPayload(
  cells: Cell[],
  columnNamesByIndex: string[],
  getCellValue: (row: number, col: number) => unknown
): Cell[] {
  if (
    columnNamesByIndex.length === 0 ||
    columnNamesByIndex[0] !== DOMAIN_COLUMN_NAME
  )
    return cells;
  const domainColName = DOMAIN_COLUMN_NAME;
  const rowsWithDomain = new Set(
    cells.filter((c) => c.column_name === domainColName).map((c) => c.row_id)
  );
  const rowsInPayload = new Set(cells.map((c) => c.row_id));
  const missing = Array.from(rowsInPayload).filter(
    (r) => !rowsWithDomain.has(r)
  );
  if (missing.length === 0) return cells;
  const result = [...cells];
  for (const row of missing) {
    const raw = getCellValue(row, 0);
    const cellValue = normalizeCellValue(raw);
    result.push({
      row_id: row,
      column_name: domainColName,
      cell_value: cellValue
    });
  }
  return result;
}

export interface RunSpreadsheetSyncOptions {
  columns?: Column[];
  viewType?: CampaignSpreadsheetViewType;
  /** When provided, merge spreadsheet vs database using spreadsheetDirtyKeys / dbPriorityKeys and only put/delete changed cells. */
  syncBufferRef?: { current: SyncBufferState | null };
  /** Set true while applying backend cells to sheet so mutation handler does not schedule another sync. */
  isApplyingSyncFromBackendRef?: { current: boolean };
  /** True when this sync was triggered by backend (e.g. enrichment sync_required). Database wins for cells it has unless spreadsheet-dirty. */
  dbTriggeredSync?: boolean;
  /** Ref to current header state; after pulling backend-only columns we read updated columnNamesByIndex/columns from here. */
  headerStateRef?: {
    current: { columnNamesByIndex: string[]; columns: Column[] } | null;
  };
  /** When backend has columns the sheet doesn't (e.g. prioritization), call this to add them to the sheet and state. */
  onBackendOnlyColumns?: (
    backendOnlyColumns: Column[],
    backendCells: Cell[]
  ) => Promise<void>;
  /** Build a full Univer cell shape (with rich text for multiline values). When omitted, plain { v, t } is used. */
  cellValueToUniverCell?: (
    rawVal: string,
    options: { colType?: string }
  ) => { p?: unknown; v: unknown; t: number; s: Record<string, unknown> };
  /** When we apply backend-source cells to the sheet, call with their keys in "rowId:colIndex" format so the enriching overlay can be cleared for those cells. */
  onEnrichingCellsApplied?: (cellKeysRowCol: Set<string>) => void;
  /** When true, skip putCells/deleteCells (no backend writes). Used during enrichment so refresh/unload does not overwrite with empty or stale data. */
  enrichmentLockedRef?: { current: boolean };
  /** Prevent frontend from overwriting backend column order when the user manually rearranged columns */
  applySheetColumnOrderFromBackend?: (
    targetColumnNames: string[]
  ) => Promise<void>;
}

/** Reorder columns */
export async function applySheetColumnOrderWithMoveCols(
  api: {
    executeCommand: (
      id: string,
      params: Record<string, unknown>
    ) => boolean | Promise<boolean> | void;
  },
  headerStateRef: {
    current: { columnNamesByIndex: string[]; columns: Column[] } | null;
  },
  targetNames: string[]
): Promise<void> {
  const n = targetNames.length;
  // Prevent infinite loop by limiting the number of operations to the theoretical maximum.
  const maxOps = n * n + 16;
  for (let ops = 0; ops < maxOps; ops++) {
    const state = headerStateRef.current;
    if (!state) return;
    const names = state.columnNamesByIndex;
    if (names.length !== n) return;
    let mismatch = -1;
    for (let i = 0; i < n; i++) {
      if (names[i] !== targetNames[i]) {
        mismatch = i;
        break;
      }
    }
    if (mismatch < 0) return;
    const wantName = targetNames[mismatch];
    const from = names.indexOf(wantName);
    if (from < 0 || from === mismatch) return;
    const cmdResult = api.executeCommand('sheet.command.move-cols', {
      fromRange: { startColumn: from, endColumn: from },
      toRange: { startColumn: mismatch, endColumn: mismatch }
    }) as unknown;
    await Promise.resolve(cmdResult);
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    );
  }
}

/**
 * Run one sync tick (always bidirectional): read sheet + backend, merge by source (spreadsheet-dirty vs DB-source), push to backend, apply DB-source to sheet.
 * Source: spreadsheetDirtyKeys = spreadsheet wins (user edit, undo, paste, etc.). dbPriorityKeys or (dbTriggeredSync && backend has cell) = DB wins.
 * Only apply to sheet cells where DB is source so we never overwrite spreadsheet-dirty cells.
 */
export async function runSpreadsheetSync(
  cid: string,
  api: SyncSheetToBackendApiLike | null,
  columnNamesByIndex: string[],
  options?: RunSpreadsheetSyncOptions
): Promise<void> {
  if (!cid?.trim() || !api) return;
  const viewType = options?.viewType;
  let columns = options?.columns;
  const syncBufferRef = options?.syncBufferRef;
  const isApplyingSyncFromBackendRef = options?.isApplyingSyncFromBackendRef;
  const dbTriggeredSync = options?.dbTriggeredSync ?? false;
  const headerStateRef = options?.headerStateRef;
  const onBackendOnlyColumns = options?.onBackendOnlyColumns;
  const cellValueToUniverCell = options?.cellValueToUniverCell;
  const onEnrichingCellsApplied = options?.onEnrichingCellsApplied;
  const enrichmentLockedRef = options?.enrichmentLockedRef;
  const applySheetColumnOrderFromBackend =
    options?.applySheetColumnOrderFromBackend;
  /** True when we applied GET /columns order via move-cols this tick; Univer can still have cell data one frame misaligned with headers, so merge uses DB by column_name for those cells. */
  let appliedBackendColumnOrderThisSync = false;
  try {
    const fWorkbook = api.getActiveWorkbook();
    const fWorksheet = fWorkbook?.getActiveSheet?.();
    if (!fWorksheet) return;
    const sheet = fWorksheet.getSheet();
    const config = sheet.getConfig();
    const rows = config.rowCount ?? 0;
    let columnNamesByIndexCurrent = columnNamesByIndex;
    // Use state column count so we don't skip columns just added via Univer (sheet config may not have updated yet).
    if (rows <= 0 || columnNamesByIndexCurrent.length <= 0) return;

    // Debounced sync schedules on Univer churn (e.g. insert-col-by-range). While enrichment or
    // prioritization locks the sheet, skip full GET/merge unless the backend triggered sync.
    if (enrichmentLockedRef?.current === true && !dbTriggeredSync) {
      return;
    }

    // Fetch backend state once for both pull and merge.
    const [columnsRes, { cells: backendCells }] = await Promise.all([
      getColumns(cid, viewType),
      getCells(cid, viewType)
    ]);
    const rawBackendColumns =
      (columnsRes as { columns?: Column[] })?.columns ?? columnsRes;
    const backendColumnsList = Array.isArray(rawBackendColumns)
      ? rawBackendColumns
      : [];
    const backendColumns = backendColumnsList.sort(
      (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
    );
    const backendNamesSet = new Set(
      backendColumns.map((c: Column) => c.col_name)
    );
    const sheetNamesSetInitial = new Set(columnNamesByIndexCurrent);

    // Pull only when sync was database-triggered (e.g. after prioritization). Otherwise we would re-add
    // columns the user just removed (backend still has them until we push the delete).
    const backendOnlyColumns = backendColumns.filter(
      (c: Column) => !sheetNamesSetInitial.has(c.col_name)
    );
    if (
      dbTriggeredSync &&
      backendOnlyColumns.length > 0 &&
      onBackendOnlyColumns
    ) {
      try {
        await onBackendOnlyColumns(backendOnlyColumns, backendCells ?? []);
        if (headerStateRef?.current) {
          columnNamesByIndexCurrent = headerStateRef.current.columnNamesByIndex;
          columns = headerStateRef.current.columns;
        }
      } catch {
        // non-fatal; proceed with current sheet state
      }
    }

    // Reconcile column schema with backend: delete backend-only columns, create sheet-only columns, then reorder.
    const sheetNamesSet = new Set(columnNamesByIndexCurrent);
    const sheetOnly: { name: string; colIdx: number }[] = [];
    for (let i = 0; i < columnNamesByIndexCurrent.length; i++) {
      const name = columnNamesByIndexCurrent[i];
      if (name != null && !backendNamesSet.has(name) && !isLockedColumn(name))
        sheetOnly.push({ name, colIdx: i });
    }
    const backendOnly = backendColumns.filter(
      (c: Column) => !sheetNamesSet.has(c.col_name)
    );

    if (sheetOnly.length === 0 && backendOnly.length === 0) {
      // No schema changes; continue with cell sync only.
    } else {
      // 1) Delete backend-only columns first.
      for (const col of backendOnly) {
        try {
          await deleteColumn(cid, col.col_name, viewType);
        } catch {
          // ignore
        }
      }

      // 2) Create sheet-only columns at their target indices.
      for (const { name, colIdx } of sheetOnly) {
        try {
          await createColumn(
            cid,
            {
              col_name: name,
              col_idx: colIdx,
              col_type: 'str',
              description: ''
            },
            viewType
          );
        } catch {
          // ignore
        }
      }
    }

    // 3) Reorder backend columns to match sheet order (same column names; e.g. user dragged columns).
    let backendColumnsForReorder: Column[] = backendColumns;
    if (sheetOnly.length > 0 || backendOnly.length > 0) {
      const columnsResFresh = await getColumns(cid, viewType);
      const rawFresh =
        (columnsResFresh as { columns?: Column[] })?.columns ?? columnsResFresh;
      backendColumnsForReorder = Array.isArray(rawFresh)
        ? rawFresh.sort(
            (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
          )
        : [];
    }

    const sortedBackendForOrder = [...backendColumnsForReorder].sort(
      (a, b) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
    );
    const backendOrderNames = sortedBackendForOrder.map((c) => c.col_name);
    const sameColumnMultiset =
      columnNamesByIndexCurrent.length === backendOrderNames.length &&
      [...columnNamesByIndexCurrent].sort().join('\u0000') ===
        [...backendOrderNames].sort().join('\u0000');
    const columnOrderDiffers =
      sameColumnMultiset &&
      columnNamesByIndexCurrent.some(
        (name, i) => name !== backendOrderNames[i]
      );
    if (
      dbTriggeredSync &&
      !enrichmentLockedRef?.current &&
      applySheetColumnOrderFromBackend &&
      columnOrderDiffers
    ) {
      await applySheetColumnOrderFromBackend(backendOrderNames);
      if (headerStateRef?.current) {
        columnNamesByIndexCurrent = headerStateRef.current.columnNamesByIndex;
        columns = headerStateRef.current.columns;
        appliedBackendColumnOrderThisSync = true;
      }
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
    }

    // Push sheet column order to backend when the user manually rearranged columns
    if (!enrichmentLockedRef?.current && !dbTriggeredSync) {
      const n = columnNamesByIndexCurrent.length;
      const maxOps = n * n + 16;
      let ops = 0;
      while (ops < maxOps) {
        const sorted = [...backendColumnsForReorder].sort(
          (a, b) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
        );
        const namesAtIdx = sorted.map((c) => c.col_name);
        let mismatch = -1;
        for (let i = 0; i < n; i++) {
          const want = columnNamesByIndexCurrent[i];
          if (want == null) continue;
          if (namesAtIdx[i] !== want) {
            mismatch = i;
            break;
          }
        }
        if (mismatch < 0) break;
        const wantName = columnNamesByIndexCurrent[mismatch];
        const currentIdx = namesAtIdx.indexOf(wantName);
        if (currentIdx < 0 || currentIdx === mismatch) break;
        try {
          await reorderColumn(
            cid,
            wantName,
            { new_col_idx: mismatch },
            viewType
          );
        } catch {
          break;
        }
        const columnsResAfter = await getColumns(cid, viewType);
        const rawAfter =
          (columnsResAfter as { columns?: Column[] })?.columns ??
          columnsResAfter;
        backendColumnsForReorder = Array.isArray(rawAfter)
          ? rawAfter.sort(
              (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
            )
          : [];
        ops += 1;
      }
    }
    if (headerStateRef?.current?.columns) {
      normalizeColumnIndices(headerStateRef.current.columns);
    }
    if (columns) {
      normalizeColumnIndices(columns);
    }

    // Snapshot buffer before reading sheet so pending spreadsheet edits are not lost to races.
    const { spreadsheetDirtyKeys, dbPriorityKeys } =
      syncBufferRef?.current != null
        ? takeSyncBufferSnapshot(syncBufferRef.current)
        : {
            spreadsheetDirtyKeys: new Set<string>(),
            dbPriorityKeys: new Set<string>()
          };

    const dbCellMap = new Map<string, Cell>();
    for (const cell of backendCells ?? []) {
      dbCellMap.set(SYNC_BUFFER_CELL_KEY(cell.row_id, cell.column_name), {
        ...cell
      });
    }

    // Re-read sheet dimensions (may have changed after pull). Read each cell via getCellValueOnly
    // so we get full cell data (including rich text p.body.dataStream); getValues() often returns
    // only the raw v, which is null for rich-text cells and would wrongly sync as empty.
    const configAfter = sheet.getConfig();
    const rowsAfter = configAfter.rowCount ?? 0;
    // Use state column count so we include columns just added via Univer (sheet config may not have updated yet).
    const colsAfter = columnNamesByIndexCurrent.length;
    if (rowsAfter <= 0 || colsAfter <= 0) return;
    const sheetCellKeys = new Set<string>();
    const sheetCellMap = new Map<string, Cell>();
    for (let r = 0; r < rowsAfter; r++) {
      for (let c = 0; c < colsAfter; c++) {
        const columnName = columnNamesByIndexCurrent[c];
        if (columnName == null) continue;
        const v =
          'getCellValueOnly' in sheet &&
          typeof sheet.getCellValueOnly === 'function'
            ? sheet.getCellValueOnly(r, c)
            : null;
        const key = SYNC_BUFFER_CELL_KEY(r, columnName);
        sheetCellKeys.add(key);
        let cellValue = normalizeCellValue(v);
        const colType = columns?.[c]
          ? getColTypeFromSchema(columns[c] as ColumnSchemaWithData)
          : undefined;
        if (normalizePythonType(colType) === 'list') {
          cellValue = normalizeListValueForBackend(cellValue);
        }
        sheetCellMap.set(key, {
          row_id: r,
          column_name: columnName,
          cell_value: cellValue
        });
      }
    }

    // After programmatic move-cols, header names match the backend but Univer's cell matrix can still
    // read misaligned at getCellValueOnly(r,c) vs columnNamesByIndex[c]. Rely on
    // backend GET /cells as the source of truth.
    if (appliedBackendColumnOrderThisSync) {
      for (let r = 0; r < rowsAfter; r++) {
        for (let c = 0; c < colsAfter; c++) {
          const columnName = columnNamesByIndexCurrent[c];
          if (columnName == null) continue;
          const key = SYNC_BUFFER_CELL_KEY(r, columnName);
          const dbCell = dbCellMap.get(key);
          if (dbCell == null) {
            // After move-cols, getCellValueOnly(r,c) can disagree with columnNamesByIndex[c]; do not keep
            // that read when GET /cells has no row — it is another column's value shown under this header.
            if (!spreadsheetDirtyKeys.has(key)) {
              sheetCellMap.set(key, {
                row_id: r,
                column_name: columnName,
                cell_value: ''
              });
            }
            continue;
          }
          let cellValue = normalizeCellValue(dbCell.cell_value ?? '');
          const colType = columns?.[c]
            ? getColTypeFromSchema(columns[c] as ColumnSchemaWithData)
            : undefined;
          if (normalizePythonType(colType) === 'list') {
            cellValue = normalizeListValueForBackend(cellValue);
          }
          sheetCellMap.set(key, {
            row_id: r,
            column_name: columnName,
            cell_value: cellValue
          });
        }
      }
    }

    // Merged state by source: spreadsheet-dirty → sheet wins, except we must not PUT blank over DB when
    // the sheet read is stale during a DB-triggered sync (e.g. sync_required after enrichment).
    const mergedMap = new Map<string, Cell>();
    const keysFromBe = new Set<string>();
    for (const key of Array.from(sheetCellKeys)) {
      const commaAt = key.indexOf(',');
      const rowIdStr = commaAt >= 0 ? key.slice(0, commaAt) : key;
      const columnName = commaAt >= 0 ? key.slice(commaAt + 1) : '';
      const rowId = parseInt(rowIdStr ?? '0', 10);
      const sheetCell = sheetCellMap.get(key);
      const dbCell = dbCellMap.get(key);
      // Programmatic move-cols can leave Univer indices misaligned from header names briefly; we may also
      // have spreadsheetDirtyKeys from prior moves. Always trust GET /cells for (row_id, column_name) here.
      if (appliedBackendColumnOrderThisSync && dbCell != null) {
        mergedMap.set(key, {
          row_id: rowId,
          column_name: columnName,
          cell_value: dbCell.cell_value ?? ''
        });
        keysFromBe.add(key);
        continue;
      }
      if (appliedBackendColumnOrderThisSync && dbCell == null) {
        if (spreadsheetDirtyKeys.has(key) && sheetCell) {
          mergedMap.set(key, {
            row_id: rowId,
            column_name: columnName,
            cell_value: sheetCell.cell_value ?? ''
          });
        } else {
          mergedMap.set(key, {
            row_id: rowId,
            column_name: columnName,
            cell_value: ''
          });
          keysFromBe.add(key);
        }
        continue;
      }
      const dbIsSource =
        dbPriorityKeys.has(key) || (dbTriggeredSync && dbCell != null);
      let chosen: Cell;
      if (spreadsheetDirtyKeys.has(key) && sheetCell) {
        const sheetVal = normalizeCellValue(sheetCell.cell_value ?? '');
        const dbVal = normalizeCellValue(dbCell?.cell_value ?? '');
        if (
          dbCell != null &&
          dbVal !== '' &&
          sheetVal === '' &&
          dbTriggeredSync
        ) {
          chosen = dbCell;
          keysFromBe.add(key);
        } else {
          chosen = sheetCell;
        }
      } else if (dbIsSource && dbCell) {
        chosen = dbCell;
        keysFromBe.add(key);
      } else if (sheetCell) {
        const sheetVal = normalizeCellValue(sheetCell.cell_value ?? '');
        const dbVal = normalizeCellValue(dbCell?.cell_value ?? '');
        if (!dbIsSource && dbCell && dbVal !== '' && sheetVal === '') {
          chosen = {
            row_id: rowId,
            column_name: columnName,
            cell_value: dbCell.cell_value ?? ''
          };
          keysFromBe.add(key);
        } else {
          chosen = sheetCell;
        }
      } else {
        chosen = { row_id: rowId, column_name: columnName, cell_value: '' };
      }
      mergedMap.set(key, {
        row_id: rowId,
        column_name: columnName,
        cell_value: chosen.cell_value ?? ''
      });
    }

    // Include backend-only cells (e.g. new prioritization column) in mergedMap so we never DELETE them.
    // Only add backend cells that lie within the sheet's current row range (row_id < rowsAfter), so when
    // the sheet has been reduced (e.g. remove-row, dedupe, undo) we don't re-add removed rows.
    for (const [key, dbCell] of Array.from(dbCellMap)) {
      if (!mergedMap.has(key) && dbCell.row_id < rowsAfter) {
        mergedMap.set(key, {
          row_id: dbCell.row_id,
          column_name: dbCell.column_name,
          cell_value: dbCell.cell_value ?? ''
        });
        keysFromBe.add(key);
      }
    }

    const mergedCellKeys = new Set(mergedMap.keys());
    const cellsToDelete = (backendCells ?? []).filter(
      (cell) =>
        !mergedCellKeys.has(SYNC_BUFFER_CELL_KEY(cell.row_id, cell.column_name))
    );
    const cellsToPut: Cell[] = [];
    for (const [key, mergedCell] of Array.from(mergedMap.entries())) {
      const dbCell = dbCellMap.get(key);
      const existingValue = dbCell?.cell_value ?? '';
      const newValue = mergedCell.cell_value ?? '';
      if (existingValue !== newValue) {
        cellsToPut.push(mergedCell);
      }
    }

    // Skip backend writes while enrichment is running (e.g. on refresh/unload) to avoid overwriting with empty or stale data.
    if (!enrichmentLockedRef?.current) {
      if (cellsToDelete.length > 0) {
        try {
          await deleteCells(cid, cellsToDelete, viewType);
        } catch {
          // ignore 404 or other errors
        }
      }
      if (cellsToPut.length > 0) {
        await putCells(cid, cellsToPut, viewType);
      }
    }

    // Apply to sheet only cells where DB is source (keysFromBe) or DB-only (new rows). Never overwrite spreadsheet-dirty cells.
    const beOnlyCells = (backendCells ?? []).filter(
      (cell) =>
        !sheetCellKeys.has(SYNC_BUFFER_CELL_KEY(cell.row_id, cell.column_name))
    );
    const hasBeSourceToApply = keysFromBe.size > 0 || beOnlyCells.length > 0;
    if (hasBeSourceToApply && 'executeCommand' in api) {
      const appliedCellKeysRowCol = new Set<string>();
      for (const key of Array.from(keysFromBe)) {
        const commaAt = key.indexOf(',');
        const rowId = parseInt(commaAt >= 0 ? key.slice(0, commaAt) : key, 10);
        const columnName = commaAt >= 0 ? key.slice(commaAt + 1) : '';
        const colIndex = columnNamesByIndexCurrent.indexOf(columnName);
        if (colIndex < 0) continue;
        const mergedCell = mergedMap.get(key);
        const val = normalizeCellValue(mergedCell?.cell_value ?? '').trim();
        if (val.length > 0) appliedCellKeysRowCol.add(`${rowId}:${colIndex}`);
      }
      for (const cell of beOnlyCells) {
        const colIndex = columnNamesByIndexCurrent.indexOf(cell.column_name);
        if (colIndex < 0) continue;
        const val = normalizeCellValue(cell.cell_value ?? '').trim();
        if (val.length > 0)
          appliedCellKeysRowCol.add(`${cell.row_id}:${colIndex}`);
      }
      if (appliedCellKeysRowCol.size > 0)
        onEnrichingCellsApplied?.(appliedCellKeysRowCol);

      if (isApplyingSyncFromBackendRef)
        isApplyingSyncFromBackendRef.current = true;
      try {
        const valueObj: Record<
          number,
          Record<
            number,
            { v?: unknown; p?: unknown; t: number; s?: Record<string, unknown> }
          >
        > = {};
        let minR = Infinity,
          minC = Infinity,
          maxR = -1,
          maxC = -1;
        const addToValueObj = (
          rowId: number,
          colIndex: number,
          cellValue: string
        ) => {
          let displayVal = normalizeCellValue(cellValue ?? '');
          const colMeta = mergeColumnWithBackend(
            colIndex,
            columnNamesByIndexCurrent,
            columns,
            backendColumns
          );
          const colType = colMeta
            ? getColTypeFromSchema(colMeta as ColumnSchemaWithData)
            : undefined;
          if (normalizePythonType(colType) === 'list') {
            displayVal = formatListValueForDisplay(displayVal);
          }
          if (!valueObj[rowId]) valueObj[rowId] = {};
          valueObj[rowId][colIndex] = cellValueToUniverCell
            ? cellValueToUniverCell(displayVal, { colType })
            : { v: displayVal, t: CellValueType.STRING };
          minR = Math.min(minR, rowId);
          minC = Math.min(minC, colIndex);
          maxR = Math.max(maxR, rowId);
          maxC = Math.max(maxC, colIndex);
        };
        for (const key of Array.from(keysFromBe)) {
          const commaAt = key.indexOf(',');
          const rowId = parseInt(
            commaAt >= 0 ? key.slice(0, commaAt) : key,
            10
          );
          const columnName = commaAt >= 0 ? key.slice(commaAt + 1) : '';
          const colIndex = columnNamesByIndexCurrent.indexOf(columnName);
          if (colIndex < 0) continue;
          const mergedCell = mergedMap.get(key);
          if (mergedCell)
            addToValueObj(rowId, colIndex, mergedCell.cell_value ?? '');
        }
        for (const cell of beOnlyCells) {
          const colIndex = columnNamesByIndexCurrent.indexOf(cell.column_name);
          if (colIndex < 0) continue;
          addToValueObj(cell.row_id, colIndex, cell.cell_value ?? '');
        }
        if (Object.keys(valueObj).length > 0 && minR <= maxR && minC <= maxC) {
          const maxRow = Math.max(
            maxR,
            ...beOnlyCells.map((c) => c.row_id),
            rowsAfter - 1
          );
          if (maxRow >= rowsAfter) {
            const sheetInstance = (api as SyncSheetToBackendApiLike)
              .getActiveWorkbook?.()
              ?.getActiveSheet?.()
              ?.getSheet?.() as unknown;
            const sheetWithSet = sheetInstance as {
              setRowCount?: (n: number) => void;
            } | null;
            if (sheetWithSet?.setRowCount) {
              sheetWithSet.setRowCount(maxRow + 1);
            }
          }
          (
            api as {
              executeCommand: (
                id: string,
                payload: { range: object; value: object }
              ) => void;
            }
          ).executeCommand('sheet.command.set-range-values', {
            range: {
              startRow: minR,
              startColumn: minC,
              endRow: maxR,
              endColumn: maxC
            },
            value: valueObj
          });
          const widthIndices: number[] = [];
          for (let c = minC; c <= maxC; c++) widthIndices.push(c);
          // Two rAFs: preset / rich-text cells may not be readable from getCellValueOnly immediately after set-range-values.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              applyColumnWidthsForIndicesFromSheet(
                api,
                columnNamesByIndexCurrent,
                columns,
                widthIndices,
                backendColumns
              );
            });
          });
        }
      } finally {
        if (isApplyingSyncFromBackendRef) {
          const ref = isApplyingSyncFromBackendRef;
          queueMicrotask(() => {
            ref.current = false;
          });
        }
      }
    }
  } catch {
    // e.g. 404 if campaign no longer exists; avoid unhandled rejection and request loops
  }
}

/**
 * Read current sheet state from Univer and push to backend (putCells).
 * Uses runSpreadsheetSync with optional buffer for spreadsheet/DB merge. When syncBufferRef is provided,
 * only changed cells are sent (incremental) and DB-priority cells (e.g. enrichment) are preserved.
 */
export async function syncSheetCellsToBackend(
  cid: string,
  api: SyncSheetToBackendApiLike | null,
  columnNamesByIndex: string[],
  options?: RunSpreadsheetSyncOptions
): Promise<void> {
  return runSpreadsheetSync(cid, api, columnNamesByIndex, options);
}

// ---------------------------------------------------------------------------
// Primary-column deduplication (URL normalization + composite key dedupe)
// ---------------------------------------------------------------------------

function normalizeDomainFromUrl(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/^https:\/\//, '')
    .replace(/^http:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .split('?')[0];
}

function normalizeLinkedinUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  let url = value.trim().replace(/\/$/, '').toLowerCase();
  if (!url || !url.includes('linkedin.com')) return null;
  let username: string;
  if (url.includes('/in/')) {
    username = url.split('/in/')[1];
  } else if (url.includes('linkedin.com/')) {
    const parts = url.split('linkedin.com/');
    username = parts[parts.length - 1];
    username = username.startsWith('in/') ? username.slice(3) : username;
  } else {
    username = url;
  }
  username = username.split('?')[0].replace(/\/$/, '');
  return `linkedin.com/in/${username}`;
}

/**
 * Normalize a primary-column value for comparison and storage: URLs use URL normalization,
 * otherwise lowercase and trim whitespace.
 */
export function normalizePrimaryColumnValue(
  columnName: string,
  value: string
): string {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const normalizedName = columnName.replace(/^\$/, '').toLowerCase();
  if (
    normalizedName === 'domain name' ||
    normalizedName === 'company' ||
    normalizedName === 'website_url' ||
    normalizedName === 'domain'
  ) {
    const n = normalizeDomainFromUrl(raw);
    return n || raw.toLowerCase();
  }
  if (normalizedName === 'lead' || normalizedName === 'linkedin_url') {
    const n = normalizeLinkedinUrl(raw);
    return n ?? raw.toLowerCase();
  }
  return raw.toLowerCase().replace(/\s+/g, ' ').trim();
}

export interface DeduplicatePrimaryColumnsResult {
  cells: Cell[];
  removedRowCount: number;
  primaryColumnsInSheet: string[];
}

export interface DeduplicatePrimaryColumnsOptions {
  /** When set (e.g. for opportunities campaigns), use only these columns as primary keys instead of PRIMARY_COLUMN_NAMES. */
  primaryColumnNamesOverride?: string[];
}

/**
 * Deduplicate rows by composite key of normalized primary columns. Primary columns present in
 * columnNamesByIndex are used; normalization is only for determining duplicates (URLs, lowercase,
 * trim). Original cell values are preserved in the output.
 * Returns deduplicated cells with row_id 0..n-1 and original values unchanged.
 */
export function deduplicatePrimaryColumns(
  cells: Cell[],
  columnNamesByIndex: string[],
  options?: DeduplicatePrimaryColumnsOptions
): DeduplicatePrimaryColumnsResult {
  const primaryInSheet = options?.primaryColumnNamesOverride?.length
    ? options.primaryColumnNamesOverride.filter((name) =>
        columnNamesByIndex.includes(name)
      )
    : PRIMARY_COLUMN_NAMES.filter((name) => columnNamesByIndex.includes(name));
  if (primaryInSheet.length === 0) {
    return { cells, removedRowCount: 0, primaryColumnsInSheet: [] };
  }

  const rowMap = new Map<number, Record<string, string>>();
  for (const c of cells) {
    let row = rowMap.get(c.row_id);
    if (!row) {
      row = {};
      rowMap.set(c.row_id, row);
    }
    row[c.column_name] = c.cell_value != null ? String(c.cell_value) : '';
  }

  const rowIds = Array.from(rowMap.keys()).sort((a, b) => a - b);
  const seenKeys = new Set<string>();
  const keptRowIds: number[] = [];

  for (const rowId of rowIds) {
    const row = rowMap.get(rowId)!;
    const keyParts = primaryInSheet.map((colName) => {
      const v = row[colName] ?? '';
      return normalizePrimaryColumnValue(colName, v);
    });
    const compositeKey = keyParts.join('\0');
    if (compositeKey === '' || keyParts.every((p) => p === '')) {
      keptRowIds.push(rowId);
      continue;
    }
    if (seenKeys.has(compositeKey)) continue;
    seenKeys.add(compositeKey);
    keptRowIds.push(rowId);
  }

  const newCells: Cell[] = [];
  let newRowId = 0;
  for (const oldRowId of keptRowIds) {
    const row = rowMap.get(oldRowId)!;
    for (const colName of columnNamesByIndex) {
      const value = row[colName] ?? '';
      newCells.push({
        row_id: newRowId,
        column_name: colName,
        cell_value: value
      });
    }
    newRowId += 1;
  }

  const removedRowCount = rowIds.length - keptRowIds.length;
  return {
    cells: newCells,
    removedRowCount,
    primaryColumnsInSheet: primaryInSheet
  };
}
